import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@shared/database.types";

type Project = Database["public"]["Tables"]["projects"]["Row"];
type ProjectInsert = Database["public"]["Tables"]["projects"]["Insert"];
type ProjectUpdate = Database["public"]["Tables"]["projects"]["Update"];

export type ProjectWithClient = Project & {
  client?: { id: string; name: string; brand_color: string | null } | null;
};

export function useProjects(clientId?: string | null) {
  const { agencyId } = useAuth();

  return useQuery({
    queryKey: ["projects", agencyId, clientId ?? null],
    enabled: !!agencyId,
    queryFn: async (): Promise<ProjectWithClient[]> => {
      let q = supabase
        .from("projects")
        .select("*, client:clients(id,name,brand_color)")
        .eq("agency_id", agencyId!)
        .order("created_at", { ascending: false });
      if (clientId) q = q.eq("client_id", clientId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ProjectWithClient[];
    },
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  const { agencyId } = useAuth();
  return useMutation({
    mutationFn: async (input: Omit<ProjectInsert, "agency_id">) => {
      if (!agencyId) throw new Error("agency_id mancante");
      const { data, error } = await supabase
        .from("projects")
        .insert({ ...input, agency_id: agencyId })
        .select()
        .single();
      if (error) throw error;
      return data as Project;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projects"] }),
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: ProjectUpdate }) => {
      const { data, error } = await supabase
        .from("projects")
        .update(patch)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as Project;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["project", vars.id] });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("projects").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projects"] }),
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: ["project", id],
    enabled: !!id,
    queryFn: async (): Promise<ProjectWithClient | null> => {
      const { data, error } = await supabase
        .from("projects")
        .select("*, client:clients(id,name,brand_color)")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return (data as ProjectWithClient) ?? null;
    },
  });
}

export interface ProjectMetrics {
  project_id: string;
  agency_id: string;
  client_id: string;
  name: string;
  status: Database["public"]["Enums"]["project_status"];
  monthly_budget: number | null;
  total_budget: number | null;
  margin_target_pct: number | null;
  commission_pct: number | null;
  hourly_rate: number | null;
  start_date: string | null;
  end_date: string | null;
  hours_logged: number;
  cost_accumulated: number;
  tasks_total: number;
  tasks_done: number;
  tasks_open: number;
  files_count: number;
}

export function useProjectMetrics(id: string | undefined) {
  return useQuery({
    queryKey: ["project-metrics", id],
    enabled: !!id,
    queryFn: async (): Promise<ProjectMetrics | null> => {
      const { data, error } = await supabase
        .from("project_metrics_v")
        .select("*")
        .eq("project_id", id!)
        .maybeSingle();
      if (error) throw error;
      return (data as ProjectMetrics) ?? null;
    },
  });
}

export function useClientProjectsMetrics(clientId: string | undefined) {
  return useQuery({
    queryKey: ["project-metrics", "client", clientId],
    enabled: !!clientId,
    queryFn: async (): Promise<ProjectMetrics[]> => {
      const { data, error } = await supabase
        .from("project_metrics_v")
        .select("*")
        .eq("client_id", clientId!);
      if (error) throw error;
      return (data ?? []) as ProjectMetrics[];
    },
  });
}
